<template>
  <v-card
    id="articleKeywords"
    outlined
    rounded
    color="background"
    class="d-flex flex-column align-center"
    style="border: unset; width: 100%"
  >
    <LucySpark
      metric="keywordDensity"
      class="align-self-end mb-1"
    />
    <v-btn
      depressed
      block
      color="primary"
      class="multi-line-button"
      @click="showDetails = !showDetails"
    >
      <v-spacer />
      <span>
        <v-scroll-y-transition mode="out-in">
          <strong :key="totalOccurrences">{{ totalOccurrences }}</strong>
        </v-scroll-y-transition>
        <span class="mx-1">|</span>
        <span class="text-wrap">{{ $t('keywords.label') }}</span>
      </span>
      <v-spacer />
      <v-icon v-if="showDetails">
        expand_less
      </v-icon>
      <v-icon v-else>
        expand_more
      </v-icon>
    </v-btn>
    <v-expand-transition>
      <div v-show="showDetails">
        <div class="d-flex flex-column align-center article-keywords">
          <div
            v-for="row in primaryRows"
            :key="'kw-' + row.index"
            class="mt-3 article-keywords__row"
          >
            <keyword-density-row
              :keyword="row.item"
              :occurrences="occurrenceCount(row.item.keyword)"
              :main="row.item.source === 'main'"
              :read-only="shareLocked"
              :deletable="!shareLocked"
              :row-index="row.index"
              :tracked-keywords="keywords"
              @remove="removeKeyword(row.index)"
            />
          </div>
          <div class="caption text--secondary px-1 mt-2">
            {{ $t('keywords.densityHint') }}
          </div>
          <div>
            <v-tooltip right>
              <template #activator="{ on }">
                <v-icon
                  class="mr-8"
                  v-on="on"
                >
                  info_outlined
                </v-icon>
              </template>
              {{ $t('keywords.toCount') }}
            </v-tooltip>
            <v-btn
              v-if="!shareLocked"
              outlined
              @click="addKeyword"
            >
              <v-icon>add</v-icon>
            </v-btn>
          </div>
          <span id="keywordInputListAfter" />
        </div>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script lang="ts">
    import Vue from 'vue';
    import {Keyword} from "@/model/Article";
    import {debounce} from "lodash-es";
    import EventsListenerMixin from "@/mixins/EventsListenerMixin";
    import KeywordDensityRow from "@/components/article/KeywordDensityRow.vue";
    import LucySpark from "@/components/lucy/LucySpark.vue";

    interface KeywordRow {
        item: Keyword;
        index: number;
    }

    export default Vue.extend({
        name: "ArticleKeywords",
        components: {KeywordDensityRow, LucySpark},
        mixins: [EventsListenerMixin],
        data: () => ({
            showDetails: false,
        }),
        computed: {
            events: function () {
                return {
                    'keywords-expand': this.expand
                };
            },
            keywords: {
                get: function (): Keyword[] {
                    return this.$store.getters.article.allKeywords;
                },
                set: function (newKeywords: Keyword[]) {
                    this.debouncedSet(newKeywords);
                }
            },
            indexedKeywords: function (): KeywordRow[] {
                return (this.keywords || []).map((item, index) => ({item, index}));
            },
            primaryRows: function (): KeywordRow[] {
                return this.indexedKeywords.filter((row) => row.item.source !== 'inflection');
            },
            shareLocked(): boolean {
                return !!this.$store.getters.sharePreview;
            },
            wordOccurrences: function (): Record<string, number> {
                return this.$store.getters.wordOccurrences;
            },
            totalOccurrences: function (): number {
                return this.$store.getters.article.allKeywords
                    .filter((keyword: Keyword) => keyword.keyword.trim().length)
                    .filter((keyword: Keyword) => keyword.checked)
                    .filter((keyword: Keyword) => keyword.source !== 'inflection')
                    .reduce((total: number, keyword: Keyword) => {
                        return total + this.occurrenceCount(keyword.keyword);
                    }, 0) || 0;
            },
        },
        methods: {
            occurrenceCount: function (phrase: string): number {
                const key = String(phrase || '').trim();
                return this.wordOccurrences[key] || this.wordOccurrences[phrase] || 0;
            },
            expand: function () {
                this.showDetails = true;
            },
            debouncedSet: function (newKeywords: Keyword[]) {
                return debounce(() => {
                    return this.$store.getters.article.allKeywords = newKeywords.map(k => k.keyword.trim());
                }, 500);
            },
            addKeyword: function () {
                this.$store.getters.article.allKeywords.push({
                    keyword: '',
                    checked: true,
                    source: 'custom'
                });
            },
            removeKeyword: function (index: number) {
                this.$store.getters.article.allKeywords.splice(index, 1);
            }
        }
    });
</script>

<style scoped lang="sass">
.article-keywords
    width: 100%
    padding: 0 8px 8px

.article-keywords__row
    width: 100%
</style>
